import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { PlayerDirectory } from "../../../../components/player/PlayerAvatar";
import GameCard from "../../components/GameCard";
import { PickedGame, SelectedGame } from "../../types/game";
import { PollsResponse, TeamPollRank } from "../../types/poll";
import { RecordsResponse } from "../../types/records";
import { PickerViewMode } from "../viewMode";
import GameWheel, { GameWheelRenderOptions } from "./GameWheel";

interface PickerGamesProps {
  games: SelectedGame[];
  picks: Record<number, number>;
  locks: Record<number, boolean>;
  onPick: (gameId: number, teamId: number) => void;
  onToggleLock: (gameId: number) => void;
  lockCount: number;
  maxLocks: number;
  showLocks: boolean;
  showGameOfTheWeek: boolean;
  gameOfTheWeek: number | undefined;
  pollsData: PollsResponse | null;
  recordsData: RecordsResponse | null;
  rules: any;
  year?: number;
  week?: number;
  leagueId?: string;
  isGameStarted: (time: string) => boolean;
  getTeamPollRank: (
    school: string,
    polls: PollsResponse | null,
  ) => TeamPollRank;
  getTeamRecord: (teamId: number, records: RecordsResponse | null) => any;
  userEmail?: string;
  viewMode?: PickerViewMode;
  onExitWheel?: () => void;
  compact?: boolean;
  pickedCount: number;
  totalGames: number;
  visiblePicks?: Record<string, PickedGame[]>;
  playerDirectory?: PlayerDirectory;
}

export const PickerGames: React.FC<PickerGamesProps> = ({
  games,
  picks,
  locks,
  onPick,
  onToggleLock,
  lockCount,
  maxLocks,
  showLocks,
  showGameOfTheWeek,
  gameOfTheWeek,
  pollsData,
  recordsData,
  rules,
  year,
  week,
  leagueId,
  isGameStarted,
  getTeamPollRank,
  getTeamRecord,
  userEmail,
  viewMode = "overview",
  onExitWheel = () => undefined,
  compact = false,
  pickedCount,
  totalGames,
  visiblePicks = {},
  playerDirectory,
}) => {
  const [activeGameId, setActiveGameId] = useState<number>();

  useEffect(() => {
    setActiveGameId((current) => {
      if (
        current !== undefined &&
        games.some((game) => Number(game.game.id) === Number(current))
      ) {
        return current;
      }

      const firstUnpicked =
        games.find((game) => picks[Number(game.game.id)] === undefined) ??
        games[0];

      return firstUnpicked ? Number(firstUnpicked.game.id) : undefined;
    });
  }, [games, picks]);

  const renderGameCard = (
    gameObj: SelectedGame,
    options?: GameWheelRenderOptions,
  ) => {
    const peek = Boolean(options?.peek);
    const gameId = Number(gameObj.game.id);
    const started = isGameStarted(gameObj.game.time);
    const revealed = started
      ? Object.entries(visiblePicks).flatMap(([email, userPicks]) => {
          const selected = userPicks.find(
            (pick) => Number(pick.gameId) === gameId,
          );
          return selected ? [{ email, pick: selected.pick }] : [];
        })
      : [];
    const homeTeamPickers = revealed
      .filter(({ pick }) => pick === gameObj.homeTeam.id)
      .map(({ email }) => email);
    const awayTeamPickers = revealed
      .filter(({ pick }) => pick === gameObj.awayTeam.id)
      .map(({ email }) => email);
    return (
    <GameCard
      game={gameObj.game}
      metadata={gameObj.metadata}
      homeTeam={gameObj.homeTeam}
      awayTeam={gameObj.awayTeam}
      pickedTeamId={picks[Number(gameObj.game.id)]}
      onPick={
        peek
          ? undefined
          : (teamId) => onPick(Number(gameObj.game.id), teamId)
      }
      isSelected={picks[Number(gameObj.game.id)] !== undefined}
      isGameOfTheWeek={
        showGameOfTheWeek && gameObj.game.id === gameOfTheWeek
      }
      isLocked={locks[Number(gameObj.game.id)] || false}
      onToggleLock={
        peek
          ? undefined
          : () => onToggleLock(Number(gameObj.game.id))
      }
      lockCount={lockCount}
      maxLocks={maxLocks}
      homeTeamPollRank={getTeamPollRank(gameObj.homeTeam.school, pollsData)}
      awayTeamPollRank={getTeamPollRank(gameObj.awayTeam.school, pollsData)}
      homeTeamRecord={getTeamRecord(gameObj.homeTeam.id, recordsData)}
      awayTeamRecord={getTeamRecord(gameObj.awayTeam.id, recordsData)}
      showLocks={!peek && showLocks}
      displayMode={started ? "disabled" : "normal"}
      showLines={Boolean(rules?.flavorLinesRules?.showGameLines !== false)}
      showScoringLine={Boolean(
        rules?.flavorLinesRules?.customGameLinesEnabled,
      )}
      showLiveLine={
        !(
          rules?.flavorLinesRules?.customGameLinesEnabled &&
          rules?.flavorLinesRules?.hideOfficialLinesWhenCustomEnabled
        )
      }
      showCustomLine={Boolean(
        rules?.flavorLinesRules?.customGameLinesEnabled,
      )}
      showFlavorText={Boolean(rules?.flavorLinesRules?.flavorTextEnabled)}
      enableEdit={false}
      editableLeagueId={leagueId}
      year={year}
      week={week}
      homeTeamPickers={homeTeamPickers}
      awayTeamPickers={awayTeamPickers}
      playerDirectory={playerDirectory}
      currentUserEmail={userEmail}
      rules={rules}
      completion={{ completed: pickedCount, total: totalGames }}
      completionSource="user"
      expansionMode={viewMode === "wheel" || compact ? "story-scroll" : "natural"}
      expandedHeight={viewMode === "wheel" ? (compact ? 676 : 810) : undefined}
      expanded={options?.expanded}
      onExpandedChange={options?.onExpandedChange}
    />
    );
  };

  if (games.length === 0) {
    return (
      <Box>
        <Typography align="center" color="text.secondary" sx={{ my: 4 }}>
          No games to display.
        </Typography>
      </Box>
    );
  }

  if (viewMode === "wheel") {
    return (
      <Box sx={{ mb: 6 }}>
        <GameWheel
          games={games}
          activeGameId={activeGameId}
          onActiveGameChange={setActiveGameId}
          onExitWheel={onExitWheel}
          renderGame={renderGameCard}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact ? "312px" : "repeat(auto-fit, 312px)",
        justifyContent: "center",
        gap: 3,
        mb: 6,
      }}
    >
      {games.map((gameObj) => (
        <Box key={gameObj.game.id} sx={{ width: 312, minWidth: 0 }}>
          {renderGameCard(gameObj)}
        </Box>
      ))}
    </Box>
  );
};
